import { type ChatMessageItem } from '../../components/chat/ChatMessage';
import {
  type AiResponse,
  type CatalogProductCard,
  type EmailDraftCardData,
  type OutfitProduct,
  type SuggestedAction,
} from '../../types/ai';
import { type AnnouncementBanner } from '../../types/announcements';

export type ConversationState = {
  generation: number;
  messages: ChatMessageItem[];
};

type TurnAction =
  | {
      type: 'turn/started';
      generation: number;
      userMessage: ChatMessageItem;
      assistantMessage: ChatMessageItem;
    }
  | { type: 'turn/chunk'; generation: number; messageId: string; chunk: string }
  | { type: 'turn/flag'; generation: number; messageId: string; flag: 'outfit' | 'product' }
  | {
      type: 'turn/suggestedActions';
      generation: number;
      messageId: string;
      actions: SuggestedAction[];
    }
  | {
      type: 'turn/outfitProduct';
      generation: number;
      messageId: string;
      slotKey: string;
      product: OutfitProduct;
      totalSlots: number;
    }
  | {
      type: 'turn/productCard';
      generation: number;
      messageId: string;
      card: OutfitProduct;
      totalExpected?: number;
    }
  | { type: 'turn/emailDraft'; generation: number; messageId: string; draft: EmailDraftCardData }
  | {
      type: 'turn/completed';
      generation: number;
      messageId: string;
      response: AiResponse;
      fallbackLanguage: string;
    }
  | { type: 'turn/failed'; generation: number; messageId: string; message: string }
  | {
      type: 'welcome/filled';
      generation: number;
      content: string;
      suggestedActions?: SuggestedAction[];
      announcement?: AnnouncementBanner;
    };

export type ConversationAction =
  | TurnAction
  | { type: 'conversation/reset'; messages: ChatMessageItem[] }
  | { type: 'note/appended'; message: ChatMessageItem }
  | { type: 'suggestedActions/cleared' }
  | { type: 'emailDraft/resolved'; status: 'sent' | 'cancelled'; note?: ChatMessageItem };

const PRODUCT_CARD_PLACEHOLDERS = 5;

const patch = (
  state: ConversationState,
  messageId: string,
  update: (message: ChatMessageItem) => ChatMessageItem,
): ConversationState => ({
  ...state,
  messages: state.messages.map((message) => (message.id === messageId ? update(message) : message)),
});

export const initialConversationState = (messages: ChatMessageItem[]): ConversationState => ({
  generation: 0,
  messages,
});

export const conversationReducer = (
  state: ConversationState,
  action: ConversationAction,
): ConversationState => {
  if (action.type === 'conversation/reset') {
    return { generation: state.generation + 1, messages: action.messages };
  }

  // Vakten på ETT ställe. En tur som redan blivit överspelad av en omstart bär en äldre
  // generation, och allt den hinner skicka in efteråt faller här.
  if ('generation' in action && action.generation !== state.generation) {
    return state;
  }

  switch (action.type) {
    case 'turn/started':
      return {
        ...state,
        messages: [...state.messages, action.userMessage, action.assistantMessage],
      };

    case 'turn/chunk':
      return patch(state, action.messageId, (message) => ({
        ...message,
        content: message.content + action.chunk,
      }));

    case 'turn/flag':
      return patch(state, action.messageId, (message) =>
        action.flag === 'outfit'
          ? { ...message, isOutfitBuilding: true }
          : {
              ...message,
              isProductSearching: true,
              searchedProducts: true,
              productCardsExpected: PRODUCT_CARD_PLACEHOLDERS,
            },
      );

    case 'turn/suggestedActions':
      return patch(state, action.messageId, (message) => ({
        ...message,
        suggestedActions: action.actions,
      }));

    case 'turn/outfitProduct':
      return patch(state, action.messageId, (message) => {
        const products = Array.isArray(message.outfitProducts) ? [...message.outfitProducts] : [];
        const index = products.findIndex(
          (candidate) => (candidate as { categoryKey?: string }).categoryKey === action.slotKey,
        );
        if (index >= 0) {
          products[index] = action.product;
        } else {
          products.push(action.product);
        }
        return {
          ...message,
          outfitProducts: products,
          outfitSlotsExpected:
            action.totalSlots > 0 ? action.totalSlots : message.outfitSlotsExpected,
        };
      });

    case 'turn/productCard':
      return patch(state, action.messageId, (message) => {
        const cards = Array.isArray(message.productCards) ? [...message.productCards] : [];
        const cardId = (action.card as { id?: string }).id;
        const index = cardId
          ? cards.findIndex((candidate) => (candidate as { id?: string }).id === cardId)
          : -1;
        // Strömmen skickar OutfitProduct, meddelandet lagrar CatalogProductCard. Casten fanns
        // före reducern och döljer en riktig formskillnad — ta bort den när strömmen skickar
        // hela kortet, inte förr.
        const nextCard = action.card as unknown as CatalogProductCard;
        if (index >= 0) {
          cards[index] = nextCard;
        } else {
          cards.push(nextCard);
        }
        return {
          ...message,
          productCards: cards,
          searchedProducts: true,
          productCardsExpected:
            action.totalExpected && action.totalExpected > 0
              ? action.totalExpected
              : message.productCardsExpected,
        };
      });

    case 'turn/emailDraft':
      return {
        ...state,
        messages: state.messages.map((message) => {
          if (message.id === action.messageId) {
            return { ...message, emailDraft: action.draft };
          }
          return message.emailDraft ? { ...message, emailDraft: undefined } : message;
        }),
      };

    case 'turn/completed': {
      const { response } = action;
      return patch(state, action.messageId, (message) => ({
        ...message,
        id: response.assistantMessageId ?? message.id,
        content: response.message,
        sources: response.sources,
        orderContext: response.orderContext ?? undefined,
        responseMode: response.responseMode,
        suppressOrderContext: response.suppressOrderContext,
        outfitProducts: response.outfitProducts ?? undefined,
        // Korten strömmade in ett i taget under turen. Saknas de i slutmetadatan är
        // det inte samma sak som att de aldrig fanns — behåll dem.
        productCards: response.productCards ?? message.productCards,
        selectedProducts: response.selectedProducts ?? undefined,
        routing: response.routing ?? undefined,
        detectedLanguage: response.detectedLanguage ?? action.fallbackLanguage,
        isOutfitBuilding: false,
        isProductSearching: false,
      }));
    }

    case 'turn/failed':
      return patch(state, action.messageId, (message) => ({
        ...message,
        content: action.message,
        status: 'error',
      }));

    case 'welcome/filled':
      return patch(state, 'welcome', (message) => ({
        ...message,
        content: action.content,
        suggestedActions:
          action.suggestedActions && action.suggestedActions.length > 0
            ? action.suggestedActions
            : message.suggestedActions,
        announcement: action.announcement,
      }));

    case 'note/appended':
      return { ...state, messages: [...state.messages, action.message] };

    case 'suggestedActions/cleared':
      return {
        ...state,
        messages: state.messages.map((message) =>
          message.suggestedActions ? { ...message, suggestedActions: undefined } : message,
        ),
      };

    case 'emailDraft/resolved': {
      const messages = state.messages.map((message) =>
        message.emailDraft
          ? {
              ...message,
              emailDraft: {
                ...message.emailDraft,
                draft: { ...message.emailDraft.draft, status: action.status },
              },
            }
          : message,
      );
      return { ...state, messages: action.note ? [...messages, action.note] : messages };
    }

    default:
      return state;
  }
};
